import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import BigNumber from "bignumber.js";
import { motion } from "framer-motion";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";
import { Splitter, SplitterPanel } from "primereact/splitter";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import FormNumberInput from "../../../components/FormNumberInput";
import NumberUtil from "../../../utility/NumberUtil";

const springConfig = { type: "spring", stiffness: 300, damping: 30 };

const FilterMultiSelectTemplate = ({ column, options }) => {
  const { t } = useTranslation();
  const safeOptions = options.map((opt) =>
    opt == null ? { label: `(${t("space")})`, value: null } : { label: String(opt), value: opt },
  );

  return (
    <div style={{ minWidth: "20rem" }}>
      <MultiSelect
        filter
        showClear
        appendTo="self"
        value={column.getFilterValue() ?? []}
        onChange={(e) => column.setFilterValue(e.value.length ? e.value : undefined)}
        options={safeOptions}
        className="w-full"
        placeholder={t("plz_chs")}
        maxSelectedLabels={1}
      />
    </div>
  );
};

const FilterInputNumber = ({ column }) => {
  const { t } = useTranslation();
  const operators = [
    { label: t("operator.gt"), value: ">" },
    { label: t("operator.gte"), value: ">=" },
    { label: t("operator.lt"), value: "<" },
    { label: t("operator.lte"), value: "<=" },
    { label: t("operator.eq"), value: "=" },
    { label: t("operator.between"), value: "between" },
  ];

  const filterValue = column.getFilterValue() || { operator: "", value: "" };

  const [localValue, setLocalValue] = useState(filterValue.value ?? "");

  useEffect(() => {
    if (filterValue?.operator) {
      const handler = setTimeout(() => {
        column.setFilterValue({
          operator: filterValue.operator,
          value: localValue,
        });
      }, 600);

      return () => clearTimeout(handler);
    }
  }, [localValue, filterValue?.operator, column]);

  useEffect(() => {
    setLocalValue(filterValue.value);
  }, [filterValue.value]);

  return (
    <div className="grid" style={{ width: "20rem" }}>
      <div className="col-12">
        <Dropdown
          optionLabel="label"
          optionValue="value"
          value={filterValue?.operator ?? ""}
          options={operators}
          placeholder={t("plz_chs")}
          onChange={(e) => {
            const op = e.value;
            const nextValue =
              op === "between"
                ? Array.isArray(localValue)
                  ? localValue
                  : ["", ""]
                : Array.isArray(localValue)
                ? ""
                : localValue;
            setLocalValue(nextValue);
            column.setFilterValue({ operator: op, value: nextValue });
          }}
          className="w-full"
        />
      </div>

      <div className="col-12">
        {filterValue.operator === "between" ? (
          <div className="flex gap-4">
            <div>
              <label className="label-text">{t("operator.min")}</label>
              <FormNumberInput
                className="w-full"
                value={localValue?.[0] ?? ""}
                onChange={(val) => setLocalValue([val, localValue?.[1] ?? ""])}
              />
            </div>
            <div>
              <label className="label-text">{t("operator.max")}</label>
              <FormNumberInput
                className="w-full"
                value={localValue?.[1] ?? ""}
                onChange={(val) => setLocalValue([localValue?.[0] ?? "", val])}
              />
            </div>
          </div>
        ) : (
          <FormNumberInput className="w-full" value={localValue ?? ""} onChange={(val) => setLocalValue(val)} />
        )}
      </div>

      <div className="col-12 flex gap-2">
        <Button
          type="button"
          className="btn btn-sm"
          label={t("button.clear")}
          onClick={() => {
            setLocalValue("");
            column.setFilterValue(null);
          }}
        />
      </div>
    </div>
  );
};

const filterInputNumberTemplate = (options) => {
  return <FilterInputNumber column={options.column} />;
};

const filterComponent = (type) => {
  switch (type) {
    case "multiSelect":
      return FilterMultiSelectTemplate;
    case "number":
      return filterInputNumberTemplate;
    default:
      return null;
  }
};

const aggregatedCellComponent = (type, leafRows, columnId) => {
  if (!leafRows || leafRows.length === 0) return "0";

  const values = leafRows.map((row) => {
    let val = row.getValue(columnId);
    val = String(val || "0").replace(/[^\d.-]/g, "");
    return new BigNumber(val || "0");
  });

  switch (type) {
    case "sum": {
      const sum = values.reduce((acc, curr) => acc.plus(curr), new BigNumber(0));
      return NumberUtil.formatWithCommas(sum.toString());
    }
    case "avg": {
      const sum = values.reduce((acc, curr) => acc.plus(curr), new BigNumber(0));
      const avg = sum.dividedBy(values.length);
      return NumberUtil.formatWithCommas(avg.toFixed(2));
    }
    case "min": {
      const min = BigNumber.min(...values);
      return NumberUtil.formatWithCommas(min.toString());
    }
    case "max": {
      const max = BigNumber.max(...values);
      return NumberUtil.formatWithCommas(max.toString());
    }
    case "count":
      return leafRows.length;
    default:
      return "";
  }
};

const TableBody = ({
  table,
  tableContainerRef,
  rowExpansionTemplate,
  groupTemplate,
  rowClassName,
  printFlag = false,
  subtotalMap = {},
  subtotalOptionsMap = {},
}) => {
  const { t } = useTranslation();
  const { rows } = table.getRowModel();

  const activeStickyIndexRef = useRef(0);
  const stickyIndexes = useMemo(() => {
    return rows.map((row, index) => (row.getIsGrouped() ? index : null)).filter((i) => i !== null);
  }, [rows]);
  const isSticky = (index) => stickyIndexes.includes(index);
  const isActiveSticky = (index) => activeStickyIndexRef.current === index;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 56,
    getScrollElement: () => tableContainerRef.current,
    measureElement: (el) => el.offsetHeight,
    overscan: printFlag ? rows.length : 15,
    rangeExtractor: useCallback(
      (range) => {
        activeStickyIndexRef.current = [...stickyIndexes].reverse().find((index) => range.startIndex >= index) ?? 0;

        const next = new Set([activeStickyIndexRef.current, ...defaultRangeExtractor(range)]);

        return [...next].sort((a, b) => a - b);
      },
      [stickyIndexes],
    ),
  });

  // 動畫載入時機
  const isResizing = !!table.getState().columnSizingInfo.isResizingColumn;
  const isScrolling = !!rowVirtualizer.isScrolling;
  const disableAnimation = printFlag || isResizing || isScrolling;
  const motionLayoutProps = disableAnimation ? { layout: false } : { layout: "position", transition: springConfig };

  // 取得群組名稱
  const getParentGroupingValues = (row) => {
    const values = [];
    let currentRow = row.getParentRow();
    while (currentRow) {
      values.push(currentRow.groupingValue);
      currentRow = currentRow.getParentRow();
    }
    return values.reverse().join(" > ");
  };

  return (
    <tbody
      className="p-datatable-tbody"
      style={{
        ...(printFlag
          ? { height: "auto", position: "static" }
          : { height: `${rowVirtualizer.getTotalSize()}px`, position: "relative" }),
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow, virtualIndex) => {
        const row = rows[virtualRow.index];
        const isGrouped = row.getIsGrouped();
        const cells = row.getVisibleCells();

        const groupCell = cells.find((c) => c.column.id === "group");

        // 小計顯示
        const isTableLastRow = virtualRow.index === rows.length - 1;
        const hasAnySubtotal = cells.some((cell) => subtotalMap[cell.column.columnDef.accessorKey] ?? false);

        // 氣體統計顯示
        // 1. 判斷是否為目前層級的最後一個
        const parentRow = row.getParentRow();
        let isLastChildOfGroup = false;
        if (parentRow) {
          const subRows = parentRow.subRows;
          isLastChildOfGroup = subRows[subRows.length - 1].id === row.id;
        }

        // 2. 判斷是否為「最深層」
        // 如果該列沒有子列，或者其子列不是分組，則代表它是最深層的成員
        const isDeepestLevel = !row.getCanExpand() || row.subRows.length === 0;

        const isStickyRow = isSticky(virtualRow.index);
        const isActiveGroupedSticky = isGrouped && isActiveSticky(virtualRow.index) && !isLastChildOfGroup;

        // 取得群組名稱
        let topParent = row.getParentRow();
        while (topParent?.getParentRow()) {
          topParent = topParent.getParentRow();
        }
        const allParentValues = getParentGroupingValues(row);

        return (
          <tr
            className={`${virtualRow.index % 2 === 0 ? "" : "p-row-odd"}`}
            key={row.id}
            data-index={virtualRow.index}
            ref={(node) => rowVirtualizer.measureElement(node)}
            style={{
              ...(isStickyRow && { zIndex: 2 }),
              ...(isActiveGroupedSticky
                ? {
                    position: "sticky",
                    top: 56,
                  }
                : printFlag
                ? {
                    position: "relative",
                  }
                : {
                    position: "absolute",
                    transform: `translateY(${virtualRow.start}px)`,
                    top: 0,
                  }),
              display: "flex",
              left: 0,
              width: "100%",
              minHeight: 56,
              flexDirection: "column",
              transition: printFlag ? "none" : "transform 0.2s ease-out",
              // ...(row.depth > 0 &&
              //   !printFlag && {
              //     animation: "growDown 0.3s ease-out",
              //   }),
              ...(printFlag && {
                breakInside: "avoid",
                pageBreakInside: "avoid",
              }),
            }}
          >
            {/* 主列 */}
            <td
              key={`main-${row.id}`}
              className={`${rowClassName(row.original)} ${row.getIsExpanded() && !isGrouped ? "expanded-row" : ""}`}
              colSpan={cells.length}
              style={{
                padding: 0,
                display: "flex",
                width: "100%",
              }}
            >
              {isGrouped ? (
                <>
                  <motion.td
                    className={`${printFlag ? "printTd" : ""}`}
                    key={groupCell.id}
                    style={{
                      width: groupCell.column.getSize(),
                      minWidth: groupCell.column.columnDef.minSize,
                      fontWeight: 600,
                    }}
                    {...motionLayoutProps}
                  >
                    {flexRender(groupCell.column.columnDef.cell, groupCell.getContext())}
                  </motion.td>
                  {cells.slice(1).map((cell) => {
                    return (
                      <motion.td
                        className={`${cell.column.columnDef?.headerClass || ""} ${printFlag ? "printTd" : ""}`}
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.columnDef.minSize,
                          fontWeight: 600,
                        }}
                        {...motionLayoutProps}
                      >
                        {cell.getIsAggregated() && (subtotalMap[cell.column.columnDef.accessorKey] ?? false)
                          ? aggregatedCellComponent(
                              subtotalOptionsMap?.[cell.column.columnDef.accessorKey],
                              cell.row.getLeafRows(),
                              cell.column.id,
                            )
                          : null}
                      </motion.td>
                    );
                  })}
                </>
              ) : (
                cells.map((cell) => (
                  <motion.td
                    className={`${cell.column.columnDef?.headerClass || ""} ${printFlag ? "printTd" : ""}`}
                    key={cell.id}
                    style={{
                      width: cell.column.getSize(),
                      minWidth: cell.column.columnDef.minSize,
                    }}
                    {...motionLayoutProps}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </motion.td>
                ))
              )}
            </td>

            {/* 展開列 */}
            {row.getIsExpanded() && !isGrouped && (
              <td
                key={`expanded-${row.id}`}
                colSpan={cells.length}
                style={{
                  padding: 0,
                  width: "100%",
                }}
              >
                <div
                  className="td row-expander"
                  style={{
                    ...(!printFlag && {
                      animation: "growDown 0.3s ease-out",
                      overflow: "hidden",
                    }),
                  }}
                >
                  {rowExpansionTemplate(row.original)}
                </div>
              </td>
            )}

            {/* 氣體統計 */}
            {isLastChildOfGroup && isDeepestLevel && subtotalMap["gasCode"] && (
              <td
                key={`gasCode-subtotal-${row.id}`}
                colSpan={cells.length}
                style={{
                  padding: 0,
                  width: "100%",
                }}
              >
                <div
                  className="td row-expander"
                  style={{
                    ...(!printFlag && {
                      animation: "growDown 0.3s ease-out",
                      overflow: "hidden",
                    }),
                  }}
                >
                  {groupTemplate(parentRow?.subRows ?? [row], allParentValues)}
                </div>
              </td>
            )}

            {/* 小計 */}
            {isTableLastRow && hasAnySubtotal && (
              <td
                key={`subtotal-${row.id}`}
                style={{
                  padding: 0,
                  display: "flex",
                  width: "100%",
                  background: "#f8f9fa",
                }}
              >
                {(() => {
                  return cells.map((cell) => (
                    <motion.div
                      className={`td ${cell.column.columnDef?.headerClass || ""}`}
                      key={`sub-${cell.id}`}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.columnDef.minSize,
                        textAlign: cell.column.columnDef.filterFn === "number" ? "right" : "left",
                        fontWeight: 600,
                      }}
                      {...motionLayoutProps}
                    >
                      {cell.column.id === "group" || cell.column.id === cells[0].column.id
                        ? t("subtotal")
                        : subtotalMap[cell.column.columnDef.accessorKey] ?? false
                        ? aggregatedCellComponent(
                            subtotalOptionsMap?.[cell.column.columnDef.accessorKey],
                            table.getFilteredRowModel().rows,
                            cell.column.id,
                          )
                        : null}
                    </motion.div>
                  ));
                })()}
              </td>
            )}

            {/* 氣體統計 */}
            {isTableLastRow && subtotalMap["gasCode"] && (
              <td
                key={`group-subtotal-${row.id}`}
                colSpan={cells.length}
                style={{
                  padding: 0,
                  width: "100%",
                }}
              >
                <div
                  className="td row-expander"
                  style={{
                    ...(!printFlag && {
                      animation: "growDown 0.3s ease-out",
                      overflow: "hidden",
                    }),
                  }}
                >
                  {groupTemplate(table.getFilteredRowModel().rows, "")}
                </div>
              </td>
            )}
          </tr>
        );
      })}
    </tbody>
  );
};

const Table = forwardRef(
  (
    {
      rowData,
      columnDefs,
      rowExpansionTemplate,
      groupTemplate,
      rowClassName,
      footerTemplate,
      maxHeight,
      printFlag,
      grouping,
      setGrouping,
      columnVisibility,
      setColumnVisibility,
      columnOrder,
      setColumnOrder,
      subtotalMap,
      subtotalOptionsMap,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const filterRefs = useRef({});
    const tableContainerRef = useRef(null);

    const [expanded, setExpanded] = useState({});
    const [sorting, setSorting] = useState([]);
    const [columnSizing, setColumnSizing] = useState({});
    const [columnFilters, setColumnFilters] = useState([]);

    // 篩選邏輯
    const filterFns = {
      multiSelect: (row, columnId, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true;
        return filterValue.includes(row.getValue(columnId));
      },
      number: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const rowValue = Number(row.getValue(columnId));
        const { operator, value } = filterValue;

        if (operator === "between") {
          if (!Array.isArray(value)) return true;
          const [min, max] = value;
          if (min !== "" && rowValue < Number(min)) return false;
          if (max !== "" && rowValue > Number(max)) return false;
          return true;
        }

        if (value === "" || value == null) return true;

        switch (operator) {
          case ">":
            return rowValue > Number(value);
          case ">=":
            return rowValue >= Number(value);
          case "<":
            return rowValue < Number(value);
          case "<=":
            return rowValue <= Number(value);
          case "=":
            return rowValue === Number(value);
          default:
            return true;
        }
      },
    };

    // 群組欄位
    const groupColumn = {
      id: "group",
      header: t("group"),
      size: 200,
      minSize: 190,
      enableSorting: false,
      cell: ({ row }) => {
        if (!row.getIsGrouped()) return null;
        return (
          <div
            key={row.id}
            className="group-header"
            style={{ cursor: "pointer", marginLeft: `${row.depth * 16}px` }}
            onClick={row.getToggleExpandedHandler()}
          >
            <i
              className={"pi pi-angle-right"}
              style={{
                display: "inline-block",
                transition: "transform 0.2s ease",
                transform: row.getIsExpanded() ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
            <span className={`group-header-text${printFlag ? "-print" : ""}`}>
              {String(row.getValue(row.groupingColumnId) ?? `(${t("space")})`)}
            </span>
            <span className="group-header-length">({row.subRows.length})</span>
          </div>
        );
      },
    };

    const computedColumns = useMemo(() => {
      if (grouping.length > 0) {
        return [groupColumn, ...columnDefs];
      }
      return columnDefs;
    }, [grouping, columnDefs, printFlag]);

    useEffect(() => {
      if (grouping.length > 0) {
        setColumnOrder((prev) => {
          if (prev.includes("group")) return prev;
          return ["group", ...prev];
        });
      } else {
        setColumnOrder((prev) => prev.filter((id) => id !== "group"));
      }
    }, [grouping, setColumnOrder]);

    const table = useReactTable({
      data: rowData,
      columns: computedColumns,
      filterFns,
      defaultColumn: { minSize: 100 },
      state: { grouping, expanded, sorting, columnSizing, columnFilters, columnVisibility, columnOrder },
      getCoreRowModel: getCoreRowModel(),
      // 列順序
      onColumnOrderChange: setColumnOrder,
      // 顯示列
      onColumnVisibilityChange: setColumnVisibility,
      // 篩選
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel(),
      // 自適應
      enableColumnResizing: true,
      columnResizeMode: "onChange",
      onColumnSizingChange: setColumnSizing,
      // 排序
      isMultiSortEvent: (e) => e.ctrlKey || e.metaKey,
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: setSorting,
      // 展開
      onExpandedChange: setExpanded,
      getRowCanExpand: (row) => !!row.original.dataList?.length,
      getExpandedRowModel: getExpandedRowModel(),
      // 群組
      groupedColumnMode: false,
      onGroupingChange: setGrouping,
      getGroupedRowModel: getGroupedRowModel(),
    });

    const isResizing = !!table.getState().columnSizingInfo.isResizingColumn;

    // 畫面滾動時關閉 OverlayPanel
    useEffect(() => {
      const handleEvent = (e) => {
        const target = e.target;

        if (target?.closest) {
          const isInsideDropdown = target.closest(".p-multiselect-panel") || target.closest(".p-dropdown-panel");
          const isInsideOverlay = target.closest(".p-overlaypanel");

          if (isInsideDropdown || isInsideOverlay) {
            return;
          }

          Object.values(filterRefs.current || {}).forEach((overlay) => {
            if (overlay) overlay.hide();
          });
        }
      };

      window.addEventListener("resize", handleEvent);
      window.addEventListener("scroll", handleEvent, true);

      return () => {
        window.removeEventListener("resize", handleEvent);
        window.removeEventListener("scroll", handleEvent, true);
      };
    }, [filterRefs]);

    // 自適應欄位寬度
    const autoSizeColumn = (header) => {
      const column = header.column;
      const table = header.getContext().table;

      // 取得標題
      const headerText = column.columnDef.header ? String(column.columnDef.header) : "";

      // 取得欄位所有值
      const cellValues = table.getPrePaginationRowModel().rows.map((row) => String(row.getValue(column.id) ?? ""));
      const allValues = [headerText, ...cellValues];

      // 計算寬度
      const maxLength = Math.max(...allValues.map((v) => v.length));
      const autoWidth = Math.min(Math.max(maxLength * 10 + 30, 100), 500);

      // 2. 更新表格的欄位寬度狀態
      table.setColumnSizing((prev) => ({
        ...prev,
        [column.id]: autoWidth,
      }));
    };

    // 對外操作
    useImperativeHandle(ref, () => ({
      toggleAllExpanded: () => {
        table.toggleAllRowsExpanded(true);
      },
      clearAllFilters: () => {
        setSorting([]);
        setColumnFilters([]);
      },
      clearSorting: () => {
        setSorting([]);
      },
      clearColumnFilters: () => {
        setColumnFilters([]);
      },
      getExpanded: () => {
        return expanded;
      },
      setExpanded: (newValue) => {
        setExpanded(newValue);
      },
    }));

    return (
      <div className="custom-datatable-container w-full p-datatable p-datatable-striped">
        <div
          className="p-datatable-wrapper"
          style={{
            ...(printFlag ? { maxHeight: "none", overflow: "visible" } : { maxHeight: maxHeight }),
            position: "relative",
          }}
          ref={tableContainerRef}
        >
          <table className="w-full p-datatable-table" style={{ width: table.getTotalSize() }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  style={{
                    display: "flex",
                    backgroundColor: "white",
                    borderBottom: "1px solid var(--gray-200)",
                  }}
                >
                  {headerGroup.headers.map((header) => {
                    return (
                      <motion.th
                        key={header.column.id}
                        colSpan={header.colSpan}
                        {...(header.column.columnDef?.headerClass && {
                          className: header.column.columnDef?.headerClass,
                        })}
                        style={{
                          display: "flex",
                          width: header.getSize(),
                          minWidth: header.column.columnDef.minSize,
                        }}
                        layout={printFlag ? false : isResizing ? false : "position"}
                        transition={printFlag ? { duration: 0 } : isResizing ? { duration: 0 } : springConfig}
                      >
                        <div className="header-row">
                          <div
                            {...(header.column.getCanSort() && {
                              className: "sortable-header",
                              onClick: header.column.getToggleSortingHandler(),
                            })}
                          >
                            {!header.isPlaceholder && (
                              <span className="header-text">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                            )}
                            {header.column.getCanSort() && (
                              <span className="header-sort-icon">
                                {{
                                  asc: <i className="pi pi-sort-amount-up-alt" />,
                                  desc: <i className="pi pi-sort-amount-down" />,
                                }[header.column.getIsSorted()] ?? <i className="pi pi-sort-alt" />}

                                {header.column.getIsSorted() && (
                                  <small className="ml-1">
                                    {table.getState().sorting.findIndex((s) => s.id === header.column.id) + 1}
                                  </small>
                                )}
                              </span>
                            )}
                          </div>

                          {header.column.getCanFilter() && (
                            <div>
                              <Button
                                type="button"
                                icon={`${header.column.getFilterValue() ? "pi pi-filter-fill" : "pi pi-filter"}`}
                                className="p-button-icon-only"
                                rounded
                                text
                                severity="secondary"
                                style={{ height: "24px", width: "24px" }}
                                onClick={(e) => {
                                  filterRefs.current[header.column.id]?.toggle(e);
                                }}
                              />
                              <OverlayPanel
                                ref={(el) => {
                                  filterRefs.current[header.column.id] = el;
                                }}
                              >
                                {flexRender(
                                  header.column.columnDef.meta?.filterComponent ??
                                    filterComponent(header.column.columnDef?.filterFn),
                                  {
                                    column: header.column,
                                    options: header.column.columnDef.meta?.filterOptions,
                                  },
                                )}
                              </OverlayPanel>
                            </div>
                          )}
                        </div>

                        {header.column.getCanResize() && (
                          <div
                            {...{
                              onDoubleClick: () => autoSizeColumn(header),
                              onMouseDown: header.getResizeHandler(),
                              onTouchStart: header.getResizeHandler(),
                              className: `resizer ${table.options.columnResizeDirection} ${
                                header.column.getIsResizing() ? "isResizing" : ""
                              }`,
                            }}
                          />
                        )}
                      </motion.th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <TableBody
              table={table}
              tableContainerRef={tableContainerRef}
              rowClassName={rowClassName}
              rowExpansionTemplate={rowExpansionTemplate}
              groupTemplate={groupTemplate}
              printFlag={printFlag}
              subtotalMap={subtotalMap}
              subtotalOptionsMap={subtotalOptionsMap}
            />
          </table>
        </div>
        {footerTemplate && (
          <div
            className="p-datatable-footer"
            style={{ borderBottomLeftRadius: "5px", border: "1px solid #e5e7eb", borderRight: "0px", borderTop: "0px" }}
          >
            {footerTemplate()}
          </div>
        )}
      </div>
    );
  },
);

const MainDataTable = ({
  tableColumn,
  rowData,
  printFileName,
  rowClassName,
  rowExpansionTemplate,
  groupTemplate,
  footerTemplate,
  maxHeight = "35rem",
  planPlaceSeq,
  setPlanPlaceSeq,
  companyPlaceList,
  approveFlag,
  approveStatus,
  setApproveStatus,
  approveStatusList,
}) => {
  const { t } = useTranslation();
  // 列印
  const printContentRef = useRef(null);
  const expandRowsRef = useRef({});
  const tableRef = useRef(null);
  const promiseResolveRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  // 選單
  const menu = useRef(null);
  // 側邊欄
  const [sidebarWidth, setSidebarWidth] = useState(300); // 初始右側寬度
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const [isResizing, setIsResizing] = useState(false);
  const [rightVisible, setRightVisible] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor));

  const [visibleMap, setVisibleMap] = useState(() =>
    Object.fromEntries(tableColumn.map((item) => [item?.accessorKey, true])),
  );
  const [visibleColumns, setVisibleColumns] = useState(tableColumn.map((item) => item?.accessorKey));
  const [groupByFields, setGroupByFields] = useState([]);
  const [groupByItems, setGroupByItems] = useState(
    tableColumn.filter((item) => {
      const visible = visibleMap[item.accessorKey] ?? true;
      return item.rowGroup && visible;
    }),
  );
  const dropdownRefs = useRef({});
  const [subtotalMap, setSubtotalMap] = useState(() => ({
    ...Object.fromEntries(tableColumn.filter((item) => item.enableSubtotal).map((item) => [item.accessorKey, false])),
    gasCode: false,
  }));
  const [subtotalOptionsMap, setSubtotalOptionsMap] = useState(() =>
    Object.fromEntries(tableColumn.filter((item) => item.enableSubtotal).map((item) => [item.accessorKey, "sum"])),
  );

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const reactToPrintFn = useReactToPrint({
    documentTitle: printFileName,
    contentRef: printContentRef,
    onBeforePrint: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        expandRowsRef.current = tableRef.current?.getExpanded();
        tableRef.current?.toggleAllExpanded();
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      tableRef.current?.setExpanded(expandRowsRef.current);
      setIsPrinting(false);
    },
  });

  const toggle = (key) => {
    setVisibleMap((prev) => {
      return { ...prev, [key]: !prev[key] };
    });
  };

  const toggleSubtotal = (key) => {
    setSubtotalMap((prev) => {
      return { ...prev, [key]: !prev[key] };
    });
  };

  // 側邊欄大小調整
  const startResizing = (e) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const movement = startXRef.current - e.clientX;

      let newWidth = startWidthRef.current + movement;
      if (newWidth < 150) newWidth = 150;
      if (newWidth > 800) newWidth = 800;

      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // 欄位拖曳匡
  const SortableComponent = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({ id });
    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} className="flex align-items-center">
        <Button
          className="p-button-icon-only"
          icon="pi pi-bars"
          rounded
          text
          style={{ cursor: "grab", maxHeight: "2rem", maxWidth: "2rem" }}
          ref={setActivatorNodeRef}
          {...listeners}
        />
        {children}
      </div>
    );
  };

  // 欄位排序拖曳
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setVisibleColumns((prev) => {
        const oldIndex = prev.indexOf(active.id);
        const newIndex = prev.indexOf(over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  // 分組排序拖曳
  const handleDragEndByGroup = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = groupByItems.findIndex((c) => c.accessorKey === active.id);
      const newIndex = groupByItems.findIndex((c) => c.accessorKey === over.id);

      const newItems = arrayMove(groupByItems, oldIndex, newIndex);
      setGroupByItems(newItems);

      const newGroupByFields = newItems
        .map((item) => item.accessorKey)
        .filter((field) => groupByFields.includes(field));
      setGroupByFields(newGroupByFields);
    }
  };

  const items = [
    {
      label: t("button.print"),
      icon: "pi pi-print",
      command: () => {
        reactToPrintFn();
      },
    },
    {
      label: `${t("button.reset")}`,
      icon: "pi pi-refresh",
      command: () => {
        tableRef.current?.clearAllFilters();
      },
    },
    {
      label: `${t("button.clear")}${t("button.sort")}`,
      icon: "pi pi-refresh",
      command: () => {
        tableRef.current?.clearSorting();
      },
    },
    {
      label: `${t("button.clear")}${t("filterSearch")}`,
      icon: "pi pi-refresh",
      command: () => {
        tableRef.current?.clearColumnFilters();
      },
    },
  ];

  const subtotalOptions = [
    {
      label: t("operator.sum"),
      value: "sum",
    },
    {
      label: t("operator.avg"),
      value: "avg",
    },
    {
      label: t("operator.count"),
      value: "count",
    },
    {
      label: t("operator.min"),
      value: "min",
    },
    {
      label: t("operator.max"),
      value: "max",
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: ".5rem", transform: "translateY(-1rem)" }}>
        {approveFlag && (
          <div className="flex" style={{ width: "180px" }}>
            <Dropdown
              showClear
              value={approveStatus}
              options={approveStatusList}
              placeholder={t("plz_chs_all")}
              className="w-full"
              onChange={(e) => {
                setApproveStatus(e.value);
              }}
            />
          </div>
        )}
        <div className="flex-1">
          <Dropdown
            showClear
            optionLabel="placeName"
            optionValue="planPlaceSeq"
            value={planPlaceSeq}
            options={companyPlaceList}
            placeholder={t("plz_chs_all")}
            className="w-full"
            onChange={(e) => {
              setPlanPlaceSeq(e.value);
            }}
          />
        </div>
        <div className="ml-auto">
          <Menu model={items} popup ref={menu} />
          <Button
            className="p-button-icon-only"
            onClick={(event) => menu.current.toggle(event)}
            icon="pi pi-ellipsis-h"
            aria-controls="popup_menu"
            rounded
            text
            aria-haspopup
          />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* 左側：表格區域 */}
        <div ref={printContentRef} style={{ flex: 1, minWidth: 0, overflow: "auto" }}>
          <Table
            ref={tableRef}
            rowData={rowData}
            columnDefs={tableColumn}
            rowExpansionTemplate={rowExpansionTemplate}
            groupTemplate={groupTemplate}
            rowClassName={rowClassName}
            footerTemplate={footerTemplate}
            printFlag={isPrinting}
            grouping={groupByFields}
            setGrouping={setGroupByFields}
            columnVisibility={visibleMap}
            setColumnVisibility={setVisibleMap}
            columnOrder={visibleColumns}
            setColumnOrder={setVisibleColumns}
            subtotalMap={subtotalMap}
            subtotalOptionsMap={subtotalOptionsMap}
            {...(!isPrinting && { maxHeight: maxHeight })}
          />
        </div>

        {/* 右側：側邊欄 */}
        {!rightVisible && (
          <>
            <div className="splitter-gutter" onMouseDown={startResizing} />
            <div
              style={{
                width: `${sidebarWidth}px`,
                minWidth: `${sidebarWidth}px`,
                display: "flex",
                border: "none",
              }}
            >
              <Splitter
                layout="vertical"
                style={{
                  width: "100%",
                  background: "#fff",
                  // zIndex: 10,
                }}
              >
                <SplitterPanel size={40} minSize={20} className="m-3" style={{ overflow: "auto" }}>
                  <div className="w-full">
                    <div className="sticky top-0 z-1 bg-white">
                      <span>
                        <i className="pi pi-align-justify mr-2" />
                        {t("column")}
                      </span>
                      <Divider className="mt-2 mb-2" />
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={visibleColumns} strategy={verticalListSortingStrategy}>
                        {visibleColumns.map((item) => {
                          const column = tableColumn
                            .filter((col) => col.accessorKey)
                            .find((c) => c.accessorKey === item);

                          if (!column) return null;

                          const visible = visibleMap[column.accessorKey] ?? true;
                          return (
                            <SortableComponent key={column.accessorKey} id={column.accessorKey}>
                              <div className="cursor-pointer" onClick={() => toggle(column.accessorKey)}>
                                <div className={`flex align-items-center  ${visible ? "" : "p-disabled"}`}>
                                  <i className={`pi ${visible ? "pi-eye" : "pi-eye-slash"} mr-2`} />
                                  <span className={`text-ellipsis`}>{column.label}</span>
                                </div>
                              </div>
                            </SortableComponent>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </div>
                </SplitterPanel>
                <SplitterPanel size={30} minSize={20} className="m-3" style={{ overflow: "auto" }}>
                  <div className="w-full">
                    <div className="sticky top-0 z-1 bg-white">
                      <span>
                        <i className="pi pi-sitemap mr-2" />
                        {t("group")}
                      </span>
                      <Divider className="mt-2 mb-2" />
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndByGroup}>
                      <SortableContext
                        items={groupByItems.map((p) => p.accessorKey)}
                        strategy={verticalListSortingStrategy}
                      >
                        {groupByItems.map((item) => {
                          return (
                            <div key={item.accessorKey} className="w-full">
                              <SortableComponent id={item.accessorKey}>
                                <Checkbox
                                  inputId={`ingredient-${item.accessorKey}`}
                                  value={item.accessorKey}
                                  checked={groupByFields.includes(item.accessorKey)}
                                  onChange={(e) => {
                                    setGroupByFields((prev) => {
                                      let newGroupBy;
                                      if (e.checked) {
                                        newGroupBy = groupByItems
                                          .map((i) => i.accessorKey)
                                          .filter((f) => prev.includes(f) || f === item.accessorKey);
                                      } else {
                                        newGroupBy = prev.filter((f) => f !== item.accessorKey);
                                      }
                                      return newGroupBy;
                                    });
                                  }}
                                />
                                <span htmlFor={`ingredient-${item.accessorKey}`} className="ml-2 text-ellipsis">
                                  {item.label}
                                </span>
                              </SortableComponent>
                            </div>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </div>
                </SplitterPanel>
                <SplitterPanel size={30} minSize={20} className="m-3" style={{ overflow: "auto" }}>
                  <div className="w-full">
                    <div className="sticky top-0 z-1 bg-white">
                      <span>
                        <i className="pi pi-calculator mr-2" />
                        {t("subtotal")}
                      </span>
                      <Divider className="mt-2 mb-2" />
                    </div>
                    <div className="ml-2">
                      {tableColumn
                        .filter((column) => column.enableSubtotal ?? false)
                        .map((column, index) => {
                          const lable = subtotalOptions.find(
                            (item) => item.value === subtotalOptionsMap[column.accessorKey],
                          )?.label;
                          return (
                            <div
                              key={column.accessorKey}
                              className={`mt-${index === 0 ? "3" : "2"} flex align-items-center `}
                            >
                              <div className="cursor-pointer" onClick={() => toggleSubtotal(column.accessorKey)}>
                                <div
                                  className={`flex align-items-center  ${
                                    subtotalMap[column.accessorKey] ? "" : "p-disabled"
                                  }`}
                                >
                                  <i
                                    className={`pi ${subtotalMap[column.accessorKey] ? "pi-eye" : "pi-eye-slash"} mr-2`}
                                  />
                                </div>
                              </div>
                              <span
                                className={`cursor-pointer text-ellipsis ${
                                  subtotalMap[column.accessorKey] ? "" : "p-disabled"
                                }`}
                                onClick={() => {
                                  if (subtotalMap[column.accessorKey]) {
                                    // 呼叫 PrimeReact Dropdown 的 show 方法
                                    dropdownRefs.current[column.accessorKey]?.show();
                                  }
                                }}
                              >
                                {column.label}
                                {lable ? `(${lable})` : ""}
                              </span>
                              <Dropdown
                                ref={(el) => (dropdownRefs.current[column.accessorKey] = el)}
                                className="no-border-dropdown-no-padding"
                                disabled={!subtotalMap[column.accessorKey]}
                                value={subtotalOptionsMap[column.accessorKey]}
                                options={subtotalOptions}
                                onChange={(e) => {
                                  const value = e.value;
                                  setSubtotalOptionsMap((prev) => ({ ...prev, [column.accessorKey]: value }));
                                }}
                              />
                            </div>
                          );
                        })}
                      <div className={`mt-2 flex align-items-center `}>
                        <div className="cursor-pointer" onClick={() => toggleSubtotal("gasCode")}>
                          <div className={`flex align-items-center  ${subtotalMap["gasCode"] ? "" : "p-disabled"}`}>
                            <i className={`pi ${subtotalMap["gasCode"] ? "pi-eye" : "pi-eye-slash"} mr-2`} />
                          </div>
                        </div>
                        <span
                          className={`cursor-pointer text-ellipsis ${subtotalMap["gasCode"] ? "" : "p-disabled"}`}
                          onClick={() => {
                            if (subtotalMap["gasCode"]) {
                              dropdownRefs.current["gasCode"]?.show();
                            }
                          }}
                        >
                          {t("gasCodeSubtotal")}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* <div className="cursor-pointer" onClick={() => toggle(column.accessorKey)}>
                    <div className={`flex align-items-center  ${visible ? "" : "p-disabled"}`}>
                      <i className={`pi ${visible ? "pi-eye" : "pi-eye-slash"} mr-2`} />
                      <span className={`text-ellipsis`}>{column.label}</span>
                    </div>
                  </div> */}
                </SplitterPanel>
              </Splitter>
            </div>
          </>
        )}

        <div className="table-siderbar">
          {/* 欄位 */}
          <div
            className={`siderbar-butoon ${rightVisible ? "" : "active"}`}
            onClick={() => setRightVisible((prev) => !prev)}
          >
            <i className="pi pi-sliders-h mb-2" />
            {`${t("button.edit")}${t("column")}`}
          </div>
        </div>
      </div>
    </>
  );
};

export default MainDataTable;
